import {
  pushStateLocationPlugin,
  servicesPlugin,
  StateDeclaration,
  TargetState,
  UIRouterReact,
  useRouter,
} from "@uirouter/react";
import { StickyStatesPlugin } from "@uirouter/sticky-states";
import { difference, intersection, isArray, noop, partition } from "lodash";
import { useEffect, useState } from "react";
import { distinctUntilChanged, filter, map, scan } from "rxjs/operators";
import { None, Option, Some } from "space-monad";
import { MenuRoute } from "./components/menu";
import { BillingRoutes } from "./pages/billing/routes";
import { BlockchainToolsRoutes } from "./pages/blockchain-tools/routes";
import { DashboardRoutes } from "./pages/dashboard/routes";
import {
  DueDiligenceRoutes,
  MerchantDueDiligenceRoutes,
} from "./pages/due-diligence/routes";
import { PageNotFoundRoutes } from "./pages/error/routes";
import { FraudRoutes } from "./pages/kyc-kyb-aml/routes";
import { LaunchConfigurationsRoutes } from "./pages/launch-configurator/routes";
import { LoginRoutes } from "./pages/login/routes";
import { MerchantsRoutes } from "./pages/merchants/routes";
import { OverlayRoutes } from "./pages/overlay/routes";
import { ParticipationsRoutes } from "./pages/participations/routes";
import { ProfileRoutes } from "./pages/profile/routes";
import { ReportIssueRoutes } from "./pages/report-issue/routes";
import { SettingsRoutes } from "./pages/settings/routes";
import { ShellRouteStates } from "./pages/shell/routes";
import { StakingRoutes } from "./pages/staking/routes";
import { TokenOnboardingRoutes } from "./pages/token-onboarding/routes";
import { ToolsRoutes } from "./pages/tools/routes";
import { UsersRoutes } from "./pages/users/routes";
import { WalletsRoutes } from "./pages/wallets/routes";
import { store } from "./store";
import { AuthActions } from "./store/auth/actions";
import { AuthDTO } from "./store/auth/models/dto";
import { Roles } from "./store/auth/models/internal";
import { isFetchableSuccess } from "./store/common/fetchable/helpers";
import { NotificationActions } from "./store/notifications/actions";
import { store$ } from "./store/utils";
import { AndOrTreeNode } from "./utils/and-or-tree";
import { any, getOption } from "./utils/utils";
import { ELearningCouponsRoutes } from "./pages/coupons/routes";
import { SubscriptionRoutes } from "./pages/subscription/routes";
import { KYCPacksRoutes } from "./pages/kyc-packs/routes";


// TODO Prevent direct navigation
export const [noRoleStates, roleSpecificStates] = partition<MenuRoute>(
  [
    ...SubscriptionRoutes.state,
    ...LoginRoutes.states,
    ...ShellRouteStates,
    ProfileRoutes.state,
    KYCPacksRoutes.state,
    ELearningCouponsRoutes.state,
    DashboardRoutes.state,
    OverlayRoutes.state,
    // ...VaultsRoutes.states,
    ...FraudRoutes.states,
    ...BlockchainToolsRoutes.states,
    ...ParticipationsRoutes.states,
    ...TokenOnboardingRoutes.states,
    ...WalletsRoutes.states,
    ...PageNotFoundRoutes.states,
    ...BillingRoutes.states,
    ...ReportIssueRoutes.states,
    ...UsersRoutes.states,
    ...MerchantsRoutes.states,
    ...StakingRoutes.states,
    ...SettingsRoutes.states,
    ...ToolsRoutes.states,
    ...MerchantDueDiligenceRoutes.states,
    ...DueDiligenceRoutes.states,
    ...LaunchConfigurationsRoutes.states,
  ],
  (state) => !(state.data.roles instanceof AndOrTreeNode)
);

export const router = new UIRouterReact();
router.urlService.config.strictMode(false);

router.plugin(servicesPlugin);
router.plugin(pushStateLocationPlugin);
router.plugin(StickyStatesPlugin);

// Registering all the router states
store$
  .pipe(
    map((x) => x.auth.login),
    filter(isFetchableSuccess),
    filter((auth) => auth.data.accessToken.authenticated),
    map((auth) => auth.data.accessToken.roles),
    distinctUntilChanged((x, y) => intersection(x, y).length === x.length),
    scan<
      typeof Roles.T[],
      {
        oldStates: MenuRoute[];
        newStates: MenuRoute[];
      }
    >(
      (acc, roles) => ({
        newStates: roleSpecificStates.filter(
          (s) => roles && s.data.roles?.exec(roles)
        ),
        oldStates: acc.newStates,
      }),
      { oldStates: [], newStates: [] }
    )
  )
  .subscribe(({ newStates, oldStates }) => {
    oldStates.forEach((s) => router.stateRegistry.deregister(s));
    newStates.forEach((s) => router.stateRegistry.register(s));
  });

noRoleStates.forEach((s) => router.stateRegistry.register(s));

router.urlService.rules.initial({ state: DashboardRoutes.name });
router.urlService.rules.otherwise({
  state: PageNotFoundRoutes.names.pageNotFound,
});

// if (process.env.NODE_ENV !== "production") {
//   const { visualizer } = require("@uirouter/visualizer");
//   visualizer(router);
//   router.trace.enable();
// }

const allStickyStates = [...noRoleStates, ...roleSpecificStates].filter(
  (state) => state.sticky
);

//Only Allow sticky states for overlay
router.transitionService.onBefore({}, (transition) => {
  const { name } = transition.to();

  // if we're coming from a sticky state
  const sourceStickyStates = intersection(
    allStickyStates
      .map((x) => x.name)
      .filter((x) => x !== undefined)
      .map((x) => x as string),
    transition.from().name?.split(".")
  );

  // if we're going to sticky state
  const destinationStickyStates = intersection(
    allStickyStates
      .map((x) => x.name)
      .filter((x) => x !== undefined)
      .map((x) => x as string),
    transition.to().name?.split(".")
  );

  // If we have diff in sticky states and we're not going to a modal
  if (
    difference(sourceStickyStates, destinationStickyStates).length > 0 &&
    !name?.startsWith("modal")
  ) {
    const emptyStickyState = isArray(transition.options().exitSticky)
      ? (transition.options().exitSticky as any[]).length < 1
      : transition.options().exitSticky === undefined;

    if (emptyStickyState) {
      if (sourceStickyStates.length > 0) {
        return transition.targetState().withOptions({
          exitSticky: sourceStickyStates,
        });
      }
    }
  }
});

// Remove transition on route change
router.transitionService.onSuccess({}, () => {
  store.dispatch(NotificationActions.clearNotification());
});

// Check if we are landing on landing = false state
router.transitionService.onBefore({ from: "" }, (transition) => {
  const opt = Option(transition.to().data);
  return opt
    .map((data) => data.landing)
    .filter((landing) => landing === false)
    .fold(noop, () => router.stateService.target(DashboardRoutes.name));
});

// Check if we are landing on an overlay or
{
  let queuedOverlay: TargetState | undefined;
  router.transitionService.onBefore(
    { to: "modal.**", from: "" },
    (transition) => {
      queuedOverlay = router.stateService.target(
        transition.to(),
        transition.params(),
        transition.options()
      );
      return router.stateService.target(
        DashboardRoutes.name,
        {},
        { location: false }
      );
    }
  );

  const unsub = router.transitionService.onSuccess({}, () => {
    if (queuedOverlay) {
      const temp = queuedOverlay;
      queuedOverlay = undefined;
      unsub();
      setTimeout(
        () =>
          router.stateService.go(temp.name(), temp.params(), temp.options()),
        0
      );
    }
  });
}

//Authentication Guard. If states are unallowed, redirect
router.transitionService.onBefore({}, (transition) => {
  const opt = Option(transition.to().data);

  return any<TargetState>([
    opt
      .map((data) => data.authed)
      .flatMap(() =>
        getOption(store.getState().auth.login)
          .filter(
            (x) =>
              !AuthDTO.Responses.Login.isWithTwoFA(x) ||
              x.accessToken.authenticated
          )
          .fold(
            () => {
              store.dispatch(AuthActions.setRedirectUrl(window.location.href));
              return Some(
                transition.router.stateService.target(LoginRoutes.names.LOGIN)
              );
            },
            () => None
          )
      ),
    opt
      .map((data) => data.unauthed)
      .flatMap(() => getOption(store.getState().auth.login))
      .flatMap((loginData) =>
        loginData.accessToken.authenticated ? Some({}) : None
      )
      .map(() => transition.router.stateService.target(DashboardRoutes.name)),
  ])
    .map((destination) => destination)
    .get();
});

//Participations guard TODO improve
router.transitionService.onBefore(
  { to: DashboardRoutes.name },
  (transition) => {
    const token = store.getState().auth.login;
    if (
      token.status === "ready" &&
      intersection(token.data.accessToken.roles, [
        "ROLE_ADMIN",
        "ROLE_MERCHANT",
        "ROLE_COMMUNITY_MANAGER",
      ]).length > 0
    ) {
      return transition.router.stateService.target(
        ParticipationsRoutes.names.all
      );
    }
  }
);

// This is overridden from the library one because the library one
// will not react to the inital router state
export function useCurrentStateAndParams() {
  const globals = useRouter().globals;
  const [stateData, setStateData] = useState<{
    state: StateDeclaration;
    params: any;
  }>({ state: globals.current, params: globals.params });

  useEffect(() => {
    setStateData({
      state: router.globals.current,
      params: router.globals.params,
    });
    const unsubscribe = router.transitionService.onSuccess({}, (transition) => {
      setStateData({ state: transition.to(), params: transition.params("to") });
    });

    return () => unsubscribe();
  }, []);

  return stateData;
}
